/**
 * @fileoverview EnrichmentPreviewPanel — displays owner parsing preview and
 * enhancement summary for dry run results.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Show parsed owner data (raw → parsed) for preview rows that have it.
 * - Show enhancement summary: which rows need enrichment and via which pathway.
 * - Allow user to see the enrichment decision reasoning.
 *
 * Dependencies:
 * - React (useState)
 * - lucide-react icons
 *
 * Architecture:
 * - Presentational component: all data arrives via props.
 * - No API calls, no Dexie reads.
 *
 * Notes:
 * - This panel is rendered in the dry run summary step when preview rows
 *   contain enrichment data (parsedOwner or pathway).
 * - The panel is collapsible to save space when not needed.
 */

import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  User,
  Sparkles,
  AlertCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract EnrichmentPreviewRow
 *
 * Purpose:
 * - Subset of PreviewRow fields needed for the enrichment preview panel.
 */
export interface EnrichmentPreviewRow {
  rowNumber: number;
  matchKeyValue: string;
  enrichment?: {
    needsEnrichment: boolean;
    enrichmentType: 'ai' | 'none';
    missingFields: string[];
    pathway?: 'ai' | 'none' | 'skip';
    parsedOwner?: {
      rawInput: string;
      parsed: Record<string, string>;
    };
  };
}

/**
 * @contract EnrichmentSummary
 *
 * Purpose:
 * - Aggregated enrichment statistics for the dry run.
 */
export interface EnrichmentSummary {
  totalRows: number;
  needsEnrichment: number;
  aiPathway: number;
  skipPathway: number;
  hasParsedOwner: number;
}

/**
 * @contract EnrichmentCounts
 *
 * Purpose:
 * - Authoritative aggregate enrichment counts computed by the backend
 *   over ALL evaluated rows (not just the capped preview sample).
 *   Mirrors ImportRunSummary.enrichmentCounts from import-core.
 *
 * Fields:
 * - evaluated:    total rows evaluated for enrichment
 * - needsEnrichment: rows flagged as needing enrichment
 * - complete:     rows already complete (no enrichment needed)
 * - ai:           rows that would be enriched via AI
 * - noProvider:   rows needing enrichment but no provider available
 */
export interface EnrichmentCounts {
  evaluated: number;
  needsEnrichment: number;
  complete: number;
  ai: number;
  noProvider: number;
}

export interface EnrichmentPreviewPanelProps {
  previewRows: EnrichmentPreviewRow[];
  /**
   * Authoritative aggregate counts over all evaluated rows. When present,
   * badge totals use these instead of recomputing from the capped preview
   * sample (which saturates at PREVIEW_ROW_CAP for large imports).
   * previewRows is still used for the per-row parsed-owner preview list.
   */
  enrichmentCounts?: EnrichmentCounts;
  /** Maximum rows to show in the parsed owner preview (default 10) */
  maxPreviewRows?: number;
}

// ---------------------------------------------------------------------------
// Helper: compute enrichment summary from preview rows
// ---------------------------------------------------------------------------

function computeEnrichmentSummary(rows: EnrichmentPreviewRow[]): EnrichmentSummary {
  let needsEnrichment = 0;
  let aiPathway = 0;
  let skipPathway = 0;
  let hasParsedOwner = 0;

  for (const row of rows) {
    if (row.enrichment?.needsEnrichment) needsEnrichment++;
    if (row.enrichment?.pathway === 'ai') aiPathway++;
    if (row.enrichment?.pathway === 'skip') skipPathway++;
    if (row.enrichment?.parsedOwner) hasParsedOwner++;
  }

  return {
    totalRows: rows.length,
    needsEnrichment,
    aiPathway,
    skipPathway,
    hasParsedOwner,
  };
}

// ---------------------------------------------------------------------------
// Sub-component: PathwayBadge
// ---------------------------------------------------------------------------

function PathwayBadge({ pathway }: { pathway: 'ai' | 'none' | 'skip' }) {
  switch (pathway) {
    case 'ai':
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-purple-100 text-purple-700">
          <Sparkles className="h-3 w-3" />
          AI
        </span>
      );
    case 'skip':
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-100 text-amber-700">
          <AlertCircle className="h-3 w-3" />
          No provider
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-500">
          None
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Sub-component: ParsedOwnerRow
// ---------------------------------------------------------------------------

function ParsedOwnerRow({ row }: { row: EnrichmentPreviewRow }) {
  const { parsedOwner } = row.enrichment ?? {};
  if (!parsedOwner) return null;

  return (
    <div className="border-b border-gray-100 last:border-b-0 py-2 px-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] text-gray-400">Row {row.rowNumber}</span>
        <span className="font-mono text-[11px] text-gray-600 truncate max-w-[200px]">
          {row.matchKeyValue}
        </span>
        {row.enrichment?.pathway && (
          <PathwayBadge pathway={row.enrichment.pathway} />
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <span className="text-gray-400 text-[10px]">Raw input:</span>
          <p className="font-mono text-gray-700 wrap-break-word">{parsedOwner.rawInput || '(empty)'}</p>
        </div>
        <div>
          <span className="text-gray-400 text-[10px]">Parsed:</span>
          <div className="space-y-0.5">
            {Object.entries(parsedOwner.parsed).map(([field, value]) => (
              <div key={field} className="flex gap-1">
                <span className="text-gray-500">{field}:</span>
                <span className="font-medium text-gray-800">{value || '(empty)'}</span>
              </div>
            ))}
            {Object.keys(parsedOwner.parsed).length === 0 && (
              <span className="text-gray-400 italic">(no parsed fields)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component: EnrichmentPreviewPanel
// ---------------------------------------------------------------------------

/**
 * @component EnrichmentPreviewPanel
 *
 * Purpose:
 * - Display owner parsing preview and enhancement summary in dry run results.
 *
 * Props:
 * - previewRows: array of preview rows with enrichment data
 * - maxPreviewRows: maximum rows to show in the parsed owner preview
 *
 * Behavior:
 * - Computes enrichment summary from preview rows.
 * - Shows collapsible sections for summary and parsed owner preview.
 * - Only renders if there's enrichment data to show.
 *
 * Data Source:
 * - local (all data via props)
 *
 * Constraints:
 * - No API calls, no Dexie reads
 */
export function EnrichmentPreviewPanel({
  previewRows,
  enrichmentCounts,
  maxPreviewRows = 10,
}: EnrichmentPreviewPanelProps): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAllParsed, setShowAllParsed] = useState(false);

  // Compute per-row stats from the preview sample (used for the
  // parsed-owner count and the don't-render gate).
  const sampleSummary = computeEnrichmentSummary(previewRows);

  // For badge totals, prefer the authoritative enrichmentCounts
  // (computed over ALL evaluated rows) when available. Fall back to
  // the sample-derived stats for older runs that don't populate it.
  // NOTE: hasParsedOwner always comes from the sample because the
  // backend aggregate doesn't track it — it's a preview-only metric.
  const needsEnrichment = enrichmentCounts?.needsEnrichment ?? sampleSummary.needsEnrichment;
  const aiPathway = enrichmentCounts?.ai ?? sampleSummary.aiPathway;
  const skipPathway = enrichmentCounts?.noProvider ?? sampleSummary.skipPathway;
  const hasParsedOwner = sampleSummary.hasParsedOwner;

  // Don't render if there's no enrichment data
  if (needsEnrichment === 0 && hasParsedOwner === 0) {
    return null;
  }

  const rowsWithParsedOwner = previewRows.filter(r => r.enrichment?.parsedOwner);
  const displayedParsedRows = showAllParsed
    ? rowsWithParsedOwner
    : rowsWithParsedOwner.slice(0, maxPreviewRows);

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden mb-4">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-orange-600" />
          <span className="text-sm font-medium text-gray-800">
            Owner Parsing & Enrichment Preview
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          {/* Summary stats */}
          <div className="flex flex-wrap gap-3 mb-4">
            {needsEnrichment > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-800 text-xs">
                <AlertCircle className="h-3.5 w-3.5" />
                {needsEnrichment.toLocaleString()} need enrichment
              </div>
            )}
            {aiPathway > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs">
                <Sparkles className="h-3.5 w-3.5" />
                {aiPathway.toLocaleString()} via AI
              </div>
            )}
            {skipPathway > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <AlertCircle className="h-3.5 w-3.5" />
                {skipPathway.toLocaleString()} no provider available
              </div>
            )}
            {hasParsedOwner > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-800 text-xs">
                <User className="h-3.5 w-3.5" />
                {hasParsedOwner} with parsed owner
              </div>
            )}
          </div>

          {/* Parsed owner preview */}
          {rowsWithParsedOwner.length > 0 && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <h4 className="text-xs font-medium text-gray-700">
                  Owner Parsing Preview
                </h4>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Shows how owner fields will be parsed. Review for accuracy before committing.
                </p>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {displayedParsedRows.map(row => (
                  <ParsedOwnerRow key={row.rowNumber} row={row} />
                ))}
              </div>
              {rowsWithParsedOwner.length > maxPreviewRows && (
                <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowAllParsed(prev => !prev)}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    {showAllParsed
                      ? 'Show fewer'
                      : `Show all ${rowsWithParsedOwner.length} rows`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Enrichment note */}
          {needsEnrichment > 0 && (
            <p className="mt-3 text-[11px] text-gray-500">
              Rows flagged for enrichment will be processed during commit.
              AI enrichment costs approximately $0.002 per row.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
